import React, { useState, useEffect, useContext } from 'react';
import logoblack from '../../images/lineageblacklogo.png';
import givepageimage from '../../images/givepage/giveimageflip.png';
import { YOUTUBE_VIDEO_ID, getYouTubeEmbedUrl } from '../../utils/constants';
import doddleImage from '../../images/givepage/doddle.png';
import doodleText from '../../images/givepage/doodleTextBlack.svg';
import { navigate } from 'gatsby';
import FAQTooltip from '../popop/faq-popup';
import { PopupfaqItems } from '../popop/faqdata';
import EmailReminderModal from '../popop/remainder';
import DonorboxForm from '../NewGive/DonorForm';
import { LJCampaignUrl } from '../../constants/donorboxcampaign';
import { AppContext } from '../../apollo/wrap-root-element';

const DonationForm = ({ onDonationComplete }) => {
  const [showReminder, setReminder] = useState(false);
  const [showFAQTooltip, setShowFAQTooltip] = useState(false);
  const [selectedFAQ, setSelectedFAQ] = useState(null);
  const [triggerElement, setTriggerElement] = useState(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const { closePopup } = useContext(AppContext);

  // Listen for messages from Donorbox iframe
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== 'https://donorbox.org') return;

      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;

        if (data.event === 'payment_complete' || data.event === 'donation_complete') {
          const donationAmount = parseFloat(data.amount || data.donation_amount || 0);

          if (donationAmount > 0 && onDonationComplete) {
            onDonationComplete(donationAmount);
          }
        }
      } catch (error) {
        console.error('Error parsing Donorbox message:', error);
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('message', handleMessage);

      return () => {
        window.removeEventListener('message', handleMessage);
      };
    }
  }, [onDonationComplete]);

  const handleFAQClick = (faq, event) => {
    const target = event.currentTarget;
    setTriggerElement(target);
    setSelectedFAQ(faq);
    setShowFAQTooltip(true);
  };

  const handleFAQSelect = (faq) => {
    setSelectedFAQ(faq);
  };

  const closeFAQTooltip = () => {
    setShowFAQTooltip(false);
    setSelectedFAQ(null);
    setTriggerElement(null);
  };

  const handleVideoPlay = () => {
    setIsVideoPlaying(true);
  };

  const handleGive = (link) => {
    if (link === 'mail-a-donation') {
      navigate('/give#faq');
    } else {
      navigate('/give');
    }
  };

  const handleFaq = () => {
    navigate('/faq');
  };

  return (
    <div className="donation-form-wrapper">
      <div className="donation-form-container">
        <div className="donation-form__content-inner">
          {/* Left Side */}
          <div className="donation-form__left">
            <div className="donation-form__video-container">
              {isVideoPlaying ? (
                <iframe
                  className="donation-form__video"
                  width="100%"
                  height="100%"
                  src={getYouTubeEmbedUrl(YOUTUBE_VIDEO_ID, {
                    autoplay: 1,
                    mute: 0,
                    loop: 1,
                    controls: 0,
                    showinfo: 0,
                    rel: 0,
                    modestbranding: 1,
                  })}
                  title="YouTube video player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              ) : (
                <>
                  <img className="donation-form__video" src={givepageimage} alt="Lineage Journey" />
                  <div className="donation-form__play-button" onClick={handleVideoPlay}>
                    <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
                      <circle cx="30" cy="30" r="30" fill="rgba(255,255,255,0.9)" />
                      <path d="M23 20L40 30L23 40V20Z" fill="#333" />
                    </svg>
                  </div>
                </>
              )}
            </div>

            <div className="donation-form__info">
              <div className="donation-form__logo">
                <img src={logoblack} alt="Lineage logo black" />
              </div>

              <h3 className="donation-form__title">On This Journey Together</h3>

              <p className="donation-form__description">
                Lineage Journey is a coordinated project and your support helps people discover their identity,
                understand their purpose and realize their destiny.
              </p>

              <div className="donation-form__buttons">
                <button onClick={handleGive} className="donation-form__btn donation-form__btn--primary">
                  Other Ways to Give
                </button>
                <button
                  onClick={() => handleGive('mail-a-donation')}
                  className="donation-form__btn donation-form__btn--secondary"
                >
                  Mail a Donation
                </button>
                <button
                  className="donation-form__btn donation-form__btn--cancel"
                  onClick={() => {
                    if (!showReminder) {
                      setReminder(true);
                    } else {
                      setReminder(false);
                      closePopup();
                      navigate('/give');
                    }
                  }}
                >
                  Cancel Donation
                </button>
              </div>
            </div>
          </div>

          {/* Right Side - Donation Form */}
          <div className="donation-form__right">
            {showReminder ? (
              <>
                <div
                  className="donation-form__email-remainder-container"
                  style={{
                    position: 'relative',
                    animation: 'slideInFromRight 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  <div className="donation-form__email-remainder">
                    <EmailReminderModal setReminder={setReminder} onClose={closePopup} />
                  </div>
                </div>
                <style>
                  {`@keyframes slideInFromRight { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }`}
                </style>
              </>
            ) : (
              <div className="donation-form__iframe-container">
                <DonorboxForm embedCode={LJCampaignUrl} width="100%" />
              </div>
            )}
            {!showReminder ? (
              <div className="donation-form__faq-link" onClick={handleFaq}>
                <p>
                  Have questions? Want to give with a check? <br />
                  Visit our FAQ.
                </p>
              </div>
            ) : null}
            {!showReminder ? (
              <div className="donation-form__handwritten-note">
                <img src={doodleText} alt="doodleText" className="handwritten-text" />
                <img src={doddleImage} alt="doodleImage" className="handwritten-doodle" />
              </div>
            ) : null}
          </div>
        </div>

        {/* FAQ Footer */}
        <div className="donation-form__footer">
          {PopupfaqItems.map((item, idx) => (
            <div key={idx} className="donation-form__faq-item">
              <button onClick={(event) => handleFAQClick(item, event)} className="donation-form__faq-btn">
                {item.question}
              </button>
              {(() => {
                if (typeof window !== 'undefined' && window.matchMedia('(max-width: 1024px)').matches) {
                  return idx !== PopupfaqItems.length - 1 && idx !== PopupfaqItems.length - 2 ? <hr /> : null;
                }
                return idx !== PopupfaqItems.length - 1 ? <hr /> : null;
              })()}
            </div>
          ))}
          <FAQTooltip
            isOpen={showFAQTooltip}
            faqItem={selectedFAQ}
            onClose={closeFAQTooltip}
            allFAQs={PopupfaqItems}
            onFAQSelect={handleFAQSelect}
            triggerElement={triggerElement}
          />
        </div>
      </div>
    </div>
  );
};

export default DonationForm;
